import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Mail, Copy, Check, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import QRCode from 'qrcode';
import { getMfaResendCooldownSeconds } from '../../utils/mfaCooldown';
import Avatar from '../ui/Avatar';

const MFASetup = ({ onSetupComplete, onCancel, userEmail, isRequired = false }) => {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);
  const [deviceName, setDeviceName] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  // Reset all state to initial values
  const resetToMethodSelection = () => {
    setStep(1);
    setMethod(null);
    setDeviceName('');
    setSetupData(null);
    setQrCodeUrl('');
    setVerificationCode('');
    setLoading(false);
    setCopied(false);
    setResendCooldown(0);
    setResending(false);
  };

  // Cooldown timer effect
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Resend email verification code
  const resendEmailCode = async () => {
    if (resendCooldown > 0 || resending) return;
    
    try {
      setResending(true);
      const response = await mfaService.resendEmailCode();
      
      if (response.cooldown_remaining_seconds) {
        setResendCooldown(getMfaResendCooldownSeconds(response));
        toast.error(response.cooldown_message || 'Please wait before requesting another code.');
      } else {
        setResendCooldown(getMfaResendCooldownSeconds(response));
        toast.success('New verification code sent to your email!');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const data = error.response.data;
        setResendCooldown(getMfaResendCooldownSeconds(data));
        toast.error(data.cooldown_message || 'Please wait before requesting another code.');
      } else {
        toast.error('Failed to resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const startSetup = async (selectedMethod) => {
    // For email verification, we don't need a device name
    if (selectedMethod === 'totp' && !deviceName.trim()) {
      toast.error('Please enter a device name for your authenticator app');
      return;
    }

    try {
      setLoading(true);
      
      // Use appropriate device name based on method
      const finalDeviceName = selectedMethod === 'email' 
        ? 'Email Verification' 
        : deviceName.trim();
      
      const data = await mfaService.setupMFA(selectedMethod, finalDeviceName);
      console.log('[MFA SETUP] Backend response:', data);
      console.log('[MFA SETUP] Selected method:', selectedMethod);
      
      // Set method and setup data together to ensure they're in sync
      setMethod(selectedMethod);
      setSetupData({ ...data, selectedMethod }); // Include selectedMethod in setupData for fallback

      if (selectedMethod === 'email') {
        setResendCooldown(getMfaResendCooldownSeconds(data));
      }
      
      if (selectedMethod === 'totp' && data.qr_code) {
        console.log('[MFA SETUP] QR code received from backend (base64):', data.qr_code.substring(0, 50) + '...');
        // Backend already provides base64 QR code, use it directly
        setQrCodeUrl(data.qr_code);
        console.log('[MFA SETUP] QR code set successfully');
      } else {
        console.log('[MFA SETUP] Email MFA setup - no QR code needed');
      }
      
      console.log('[MFA SETUP] Advancing to step 2 with method:', selectedMethod);
      setStep(2);
    } catch (error) {
      console.error('Error starting MFA setup:', error);
      toast.error('Failed to start MFA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      await mfaService.verifyMFASetup(verificationCode, method);
      toast.success('MFA setup completed successfully!');
      onSetupComplete();
    } catch (error) {
      console.error('Error verifying MFA setup:', error);
      toast.error('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={(e) => {
        // Only allow closing by clicking backdrop if MFA is not required
        if (e.target === e.currentTarget && onCancel && !isRequired) {
          onCancel();
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="flex items-center gap-2.5">
            <Shield className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Set Up Multi-Factor Authentication
            </h2>
          </div>
          {onCancel && !isRequired && (
            <button
              onClick={onCancel}
              className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {step === 1 && !method && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4 px-6 pb-6 pt-3"
          >
            <div className={`flex items-start gap-2.5 rounded-lg border px-4 py-3.5 ${
              isRequired 
                ? 'border-danger-500/25 bg-danger-50 dark:bg-danger-500/10' 
                : 'border-warning-500/30 bg-warning-50 dark:bg-warning-500/10'
            }`}>
              <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                isRequired ? 'text-danger-600 dark:text-danger-400' : 'text-warning-600 dark:text-warning-400'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  isRequired ? 'text-danger-700 dark:text-danger-400' : 'text-warning-800 dark:text-warning-400'
                }`}>
                  {isRequired ? 'MFA Setup Required - Cannot Skip' : 'MFA Required'}
                </p>
                <p className={`mt-1 text-sm ${
                  isRequired ? 'text-danger-700 dark:text-danger-400' : 'text-warning-700 dark:text-warning-400'
                }`}>
                  {isRequired 
                    ? 'You must complete multi-factor authentication setup to continue. This step cannot be skipped.'
                    : 'Your account requires multi-factor authentication for enhanced security. Please choose a method to set up MFA.'
                  }
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => setMethod('totp')}
                disabled={loading}
                className="group w-full rounded-xl border border-gray-200/80 p-4 text-left transition-colors hover:border-gray-300 hover:bg-gray-50/80 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:hover:border-white/15 dark:hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3">
                  <Avatar icon={Smartphone} size="md" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Authenticator App</h3>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                      Use Google Authenticator, Authy, or similar apps
                    </p>
                  </div>
                  <ArrowRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                </div>
              </button>

              <button
                onClick={() => startSetup('email')}
                disabled={loading}
                className="group w-full rounded-xl border border-gray-200/80 p-4 text-left transition-colors hover:border-gray-300 hover:bg-gray-50/80 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:hover:border-white/15 dark:hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3">
                  <Avatar icon={Mail} size="md" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Email Verification</h3>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                      Receive codes via email at {userEmail}
                    </p>
                  </div>
                  <ArrowRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                </div>
              </button>
            </div>

            {loading && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
              </div>
            )}
          </motion.div>
        )}

        {/* Step 1.5: Device Name for TOTP only */}
        {step === 1 && method === 'totp' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="space-y-5 px-6 pb-6 pt-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Name Your Device
                </h3>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  Give your authenticator device a memorable name
                </p>
              </div>

              <div>
                <label htmlFor="deviceName" className="label">
                  Device Name
                </label>
                <input
                  id="deviceName"
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g., My iPhone, Work Laptop, Personal Computer"
                  className="input"
                  maxLength={50}
                  autoFocus
                />
                <p className="hint">
                  This helps you identify this device later when managing your MFA methods.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                onClick={resetToMethodSelection}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={() => startSetup('totp')}
                disabled={loading || !deviceName.trim()}
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && method === 'totp' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="space-y-5 px-6 pb-6 pt-3">
              <div className="text-center">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Scan QR Code
                </h3>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  Scan this QR code with your authenticator app
                </p>
                
                {qrCodeUrl && (
                  <div className="mt-4 flex justify-center">
                    <img src={qrCodeUrl} alt="QR Code" className="h-48 w-48 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-white" />
                  </div>
                )}

                {setupData?.manual_entry_key && (
                  <div className="mt-4 rounded-md bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="break-all rounded border border-gray-200 bg-white px-2 py-1 font-mono text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                        {setupData.manual_entry_key}
                      </code>
                      <button
                        onClick={() => copyToClipboard(setupData.manual_entry_key)}
                        className="btn btn-ghost btn-sm btn-icon flex-shrink-0"
                      >
                        {copied ? <Check className="h-4 w-4 text-success-600 dark:text-success-400" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="label">
                  Enter verification code from your app:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="input h-11 text-center font-mono text-lg tracking-[0.3em]"
                  maxLength={6}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                onClick={resetToMethodSelection}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={verifySetup}
                disabled={loading || verificationCode.length !== 6}
                className="btn btn-primary"
              >
                {loading ? 'Verifying...' : 'Complete Setup'}
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (method === 'email' || setupData?.selectedMethod === 'email') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="space-y-5 px-6 pb-6 pt-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Email Verification Setup
                </h3>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  A verification code has been sent to {userEmail}
                </p>
              </div>

              <div>
                <label className="label">
                  Enter the verification code from your email:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="input h-11 text-center font-mono text-lg tracking-[0.3em]"
                  maxLength={6}
                />
              </div>

              {/* Resend Code Section */}
              <div className="text-center">
                <button
                  onClick={resendEmailCode}
                  disabled={resending || resendCooldown > 0}
                  className="text-sm font-medium text-primary-600 underline-offset-4 hover:text-primary-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline dark:text-primary-400 dark:hover:text-primary-300 dark:disabled:text-gray-500"
                >
                  {resending ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Sending...
                    </span>
                  ) : resendCooldown > 0 ? (
                    `Resend code in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}`
                  ) : (
                    'Resend verification code'
                  )}
                </button>
                {resendCooldown === 0 && !resending && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Didn't receive the code? Click above to resend.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                onClick={resetToMethodSelection}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={verifySetup}
                disabled={loading || verificationCode.length !== 6}
                className="btn btn-primary"
              >
                {loading ? 'Verifying...' : 'Complete Setup'}
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default MFASetup;
